/**
 * API Integration Test Page
 * Simple page to test backend integration
 */
import React, { useState } from 'react';
import authService from '../services/authService';
import hospitalService, { Hospital } from '../services/hospitalService';
import productService, { Product } from '../services/productService';
import { getDevLoginCredentials } from '../config/devLogin';

const APITestPage: React.FC = () => {
  const [status, setStatus] = useState<string>('');
  const [user, setUser] = useState<any>(null);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string>('');

  const testLogin = async () => {
    setStatus('Testing login...');
    setError('');
    try {
      // ⛔ SEGURANÇA (29/07): credencial saiu do código — ia no bundle público.
      // Ver config/devLogin.ts.
      const devLogin = getDevLoginCredentials();
      if (!devLogin) {
        setError(
          '❌ Configure VITE_DEV_LOGIN_EMAIL e VITE_DEV_LOGIN_PASSWORD em ' +
          'frontend/.env.local para usar esta tela.',
        );
        setStatus('');
        return;
      }
      const result = await authService.login(devLogin);
      setUser(authService.getCurrentUser());
      setStatus('✅ Login successful!');
      console.log('Login result:', result);
    } catch (err: any) {
      setError(`❌ Login failed: ${err.message}`);
      setStatus('');
    }
  };

  const testHospitals = async () => {
    setStatus('Fetching hospitals...');
    setError('');
    try {
      const data = await hospitalService.listHospitais();
      setHospitals(data);
      setStatus(`✅ Loaded ${data.length} hospitals`);
    } catch (err: any) {
      setError(`❌ Failed to load hospitals: ${err.message}`);
      setStatus('');
    }
  };

  const testProducts = async () => {
    setStatus('Fetching products...');
    setError('');
    try {
      const data = await productService.getProducts();
      setProducts(data);
      setStatus(`✅ Loaded ${data.length} products`);
    } catch (err: any) {
      setError(`❌ Failed to load products: ${err.message}`);
      setStatus('');
    }
  };

  const testLogout = () => {
    authService.logout();
    setUser(null);
    setHospitals([]);
    setProducts([]);
    setStatus('✅ Logged out');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🧪 API Integration Test</h1>

        {/* Status */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h2 className="text-xl font-semibold mb-4">Status</h2>
          {status && <p className="text-green-600 font-medium">{status}</p>}
          {error && <p className="text-red-600 font-medium">{error}</p>}
          {user && (
            <div className="mt-4 p-4 bg-blue-50 rounded">
              <p className="font-semibold">Logged in as:</p>
              <p>Email: {user.email}</p>
              <p>Role: {user.role}</p>
            </div>
          )}
        </div>

        {/* Test Buttons */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h2 className="text-xl font-semibold mb-4">Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={testLogin}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium"
            >
              1. Test Login
            </button>
            <button
              onClick={testHospitals}
              disabled={!user}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              2. Load Hospitals
            </button>
            <button
              onClick={testProducts}
              disabled={!user}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              3. Load Products
            </button>
            <button
              onClick={testLogout}
              disabled={!user}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              4. Logout
            </button>
          </div>
        </div>

        {/* Hospitals */}
        {hospitals.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h2 className="text-xl font-semibold mb-4">Hospitals ({hospitals.length})</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Código</th>
                    <th className="p-2 text-left">Nome</th>
                    <th className="p-2 text-left">Contato</th>
                    <th className="p-2 text-left">Telefone</th>
                  </tr>
                </thead>
                <tbody>
                  {hospitals.slice(0, 5).map((hospital) => (
                    <tr key={hospital.id} className="border-t">
                      <td className="p-2">{hospital.codigo}</td>
                      <td className="p-2 font-medium">{hospital.nome_fantasia}</td>
                      <td className="p-2">{hospital.contato || '-'}</td>
                      <td className="p-2">{hospital.telefone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {hospitals.length > 5 && (
                <p className="text-gray-500 text-sm mt-2">
                  ... and {hospitals.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}

        {/* Products */}
        {products.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Products ({products.length})</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">Código</th>
                    <th className="p-2 text-left">Nome</th>
                    <th className="p-2 text-left">Categoria</th>
                    <th className="p-2 text-left">Preço</th>
                  </tr>
                </thead>
                <tbody>
                  {products.slice(0, 5).map((product) => (
                    <tr key={product.id} className="border-t">
                      <td className="p-2">{product.codigo}</td>
                      <td className="p-2 font-medium">{product.nome}</td>
                      <td className="p-2">{product.categoria || '-'}</td>
                      <td className="p-2">R$ {product.preco_unitario.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.length > 5 && (
                <p className="text-gray-500 text-sm mt-2">
                  ... and {products.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default APITestPage;
